/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert, Button, Card, Col, DatePicker, Flex, Form, Input, Modal,
  Row, Select, Space, Table, Tabs, Tag, Typography, message,
} from "antd";
import { SaveFilled, PlusOutlined, ArrowLeftOutlined, NumberOutlined, ImportOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { detailColumns, emptyDetailRow, invCodeOf, isDraft, proformaHeader } from "./model";
import ProformaService from "../../../service/ERP/Proforma.service";
import OptionService from "../../../service/ERP/Options.service";
import ModalPickProduct from "../../../components/modal/ERP/proforma/ModalPickProduct";
import ModalPickInvoice from "../../../components/modal/ERP/proforma/ModalPickInvoice";
import "../invoice/MyPage.css";

const piService = ProformaService();
const opService = OptionService();

const gotoFrom = "/ERP/proforma";
const CURRENCIES = ["USD", "EUR", "THB", "SGD", "GBP", "JPY"];

const num = (v) => Number(v || 0);

export default function ProformaManage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = location.state || {};
  const isEdit = config?.action === "edit";
  /* โหมดดูอย่างเดียว — ปิดการแก้ไขทั้งฟอร์ม ส่อนปุ่มบันทึก/ลบแถว */
  const isView = config?.action === "view";
  const piCode = config?.code;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState([]);
  const [banks, setBanks] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [head, setHead] = useState({
    pi_code: null,
    runno: null,
    source_type: config?.source === "INVOICE" ? "INVOICE" : "MANUAL",
    inv_code: null,
    inv_reserved: 0,
    is_draft: config?.source === "INVOICE" ? 0 : 1,
  });

  const [pickIndex, setPickIndex] = useState(-1);
  const [openProduct, setOpenProduct] = useState(false);
  const [openInvoice, setOpenInvoice] = useState(config?.source === "INVOICE" && !isEdit);

  const currency = Form.useWatch("currency", form) || "USD";

  /* ---------- โหลดข้อมูล ---------- */
  useEffect(() => {
    opService
      .optionsCustomer()
      .then((res) =>
        setCustomers(
          (res.data?.data || []).map((c) => ({
            value: c.cuscode,
            label: `${c.cuscode} — ${c.cusname}`,
            cusname: c.cusname,
            currency: c.currency,
          }))
        )
      )
      .catch(() => {});
  }, []);

  /* โหลดเอกสาร — ผูกกับ config เพราะหลังบันทึกใบใหม่จะสลับมาโหมดแก้ไขในหน้าเดิม */
  useEffect(() => {
    if ((isEdit || isView) && piCode) {
      loadDocument();
    } else {
      form.setFieldsValue({ ...proformaHeader, pi_date: dayjs() });
    }
  }, [config?.action, config?.code]);

  const loadDocument = async () => {
    setLoading(true);
    try {
      const res = await piService.get(piCode);
      const { header, detail: rows } = res.data?.data || {};
      setHead({
        pi_code: header?.pi_code,
        runno: header?.runno,
        source_type: header?.source_type || "MANUAL",
        inv_code: header?.inv_code,
        inv_reserved: Number(header?.inv_reserved || 0),
        is_draft: isDraft(header) ? 1 : 0,
      });
      form.setFieldsValue({
        ...proformaHeader,
        ...header,
        pi_date: header?.pi_date ? dayjs(header.pi_date) : null,
        etd: header?.etd ? dayjs(header.etd) : null,
        eta: header?.eta ? dayjs(header.eta) : null,
      });
      setDetail(
        (rows || []).map((r, i) => ({
          ...r,
          _rid: `db-${r.id ?? i}`,
          seq: i + 1,
        }))
      );
      setBanks(header?.banks || []);
    } catch (err) {
      message.error(err?.response?.data?.message || "โหลดข้อมูลไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- วิธีที่ 2 : ดึงจาก Invoice ---------- */
  const handlePickInvoice = async (record) => {
    setLoading(true);
    try {
      const res = await piService.prefillFromInvoice(record.inv_code);
      const { header, detail: rows, banks: bk } = res.data?.data || {};
      setHead((h) => ({
        ...h,
        pi_code: header?.pi_code,
        runno: header?.runno,
        source_type: "INVOICE",
        inv_code: header?.inv_code,
        is_draft: 0,
      }));
      form.setFieldsValue({
        ...proformaHeader,
        ...header,
        pi_date: header?.pi_date ? dayjs(header.pi_date) : dayjs(),
        etd: header?.etd ? dayjs(header.etd) : null,
        eta: header?.eta ? dayjs(header.eta) : null,
      });
      setDetail(
        (rows || []).map((r, i) => ({ ...r, _rid: `inv-${i}`, seq: i + 1 }))
      );
      setBanks(bk || []);
      message.success(`ดึงข้อมูลจาก ${record.inv_code} แล้ว`);
    } catch (err) {
      message.error(err?.response?.data?.message || "ดึงข้อมูลไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- ตารางสินค้า ---------- */
  const recalc = (row) => {
    const qty = num(row.unit_cs) * num(row.carton);
    return { ...row, total_qty: qty, line_total: qty * num(row.unit_price) };
  };

  const updateDetail = (index, field, value) => {
    setDetail((prev) =>
      prev.map((r, i) => (i === index ? recalc({ ...r, [field]: value }) : r))
    );
  };

  const deleteDetail = (index) => {
    setDetail((prev) => prev.filter((_, i) => i !== index).map((r, i) => ({ ...r, seq: i + 1 })));
  };

  const addRow = () => setDetail((prev) => [...prev, emptyDetailRow(prev.length + 1)]);

  const openPicker = (index) => {
    setPickIndex(index);
    setOpenProduct(true);
  };

  const handlePickProduct = (record) => {
    if (pickIndex < 0) return;
    setDetail((prev) =>
      prev.map((r, i) =>
        i === pickIndex
          ? recalc({
              ...r,
              pg_code: record.pg_code,
              pg_name: r.pg_name?.trim() ? r.pg_name : record.pg_ivname || record.pg_name,
              unit_cs: r.unit_cs ?? record.unit_cs,
            })
          : r
      )
    );
    setPickIndex(-1);
  };

  const total = useMemo(() => detail.reduce((s, r) => s + num(r.line_total), 0), [detail]);

  /* ---------- บันทึก ---------- */
  const buildPayload = (v) => ({
    header: {
      ...v,
      pi_code: head.pi_code,
      source_type: head.source_type,
      inv_code: head.source_type === "INVOICE" ? head.inv_code : null,
      pi_date: v.pi_date ? dayjs(v.pi_date).format("YYYY-MM-DD") : null,
      etd: v.etd ? dayjs(v.etd).format("YYYY-MM-DD") : null,
      eta: v.eta ? dayjs(v.eta).format("YYYY-MM-DD") : null,
    },
    detail: detail.map((r, i) => ({ ...r, seq: i + 1 })),
    banks,
  });

  /* issueAfter = true → บันทึกแล้วออกเลขเอกสารให้เลย */
  const handleSave = async (issueAfter = false) => {
    const v = await form.validateFields().catch(() => null);
    if (!v) return;

    if (detail.length === 0) {
      message.warning("กรุณาเพิ่มรายการสินค้าอย่างน้อย 1 รายการ");
      return;
    }
    const noName = detail.findIndex((r) => !String(r.pg_name || "").trim());
    if (noName > -1) {
      message.warning(`รายการที่ ${noName + 1} ยังไม่ได้กรอกชื่อสินค้า`);
      return;
    }

    setLoading(true);
    try {
      const payload = buildPayload(v);
      if (isEdit) {
        await piService.update(payload);
        message.success("บันทึกสำเร็จ");
        loadDocument();
      } else {
        const res = await piService.create(payload);
        const newCode = res.data?.data?.code;

        if (issueAfter && newCode) {
          const rs = await piService.reserveInvoice(newCode);
          const d = rs.data?.data || {};
          message.success(`บันทึกและออกเลข ${d.pi_code} แล้ว (จอง ${d.inv_code} ไว้ให้)`);
          navigate(gotoFrom, { replace: true });
          return;
        }

        message.success("บันทึกเป็นใบร่างแล้ว — กดออกเลขเอกสารเมื่อพร้อม");
        /* อยู่หน้าเดิมในโหมดแก้ไข เพื่อให้ปุ่มออกเลขเอกสารใช้งานได้ทันที */
        navigate("../manage/edit", {
          replace: true,
          state: {
            config: {
              title: "แก้ไข Proforma Invoice",
              action: "edit",
              code: newCode,
            },
          },
        });
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "บันทึกไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- ออกเลขเอกสาร / จองเลข Invoice ---------- */
  const handleReserve = () => {
    if (!head.pi_code || !isEdit) {
      message.info("บันทึกเอกสารก่อน แล้วจึงออกเลขได้");
      return;
    }
    const reserved = Number(head.inv_reserved) === 1;
    const draft = Number(head.is_draft) === 1;
    const target = invCodeOf(head.runno);

    Modal.confirm({
      title: reserved ? "ยกเลิกการจองเลข Invoice" : draft ? "ออกเลขเอกสาร" : "จองเลข Invoice",
      content: reserved
        ? `ยกเลิกการจองเลข ${target} ใช่หรือไม่?`
        : draft
        ? "ระบบจะออกเลข PI ให้ใบนี้ พร้อมจองเลข Invoice ที่คู่กัน — ออกแล้วเปลี่ยนกลับเป็นใบร่างไม่ได้"
        : `จองเลข ${target} ไว้ให้ ${head.pi_code} — เมื่อออก Invoice จากใบนี้จะได้เลขนี้`,
      okText: reserved ? "ยกเลิกการจอง" : draft ? "ออกเลขเอกสาร" : "จองเลข",
      cancelText: "ปิด",
      okButtonProps: reserved ? { danger: true } : {},
      onOk: async () => {
        try {
          if (reserved) {
            await piService.cancelReserve(head.pi_code);
            message.success("ยกเลิกการจองแล้ว");
            loadDocument();
            return;
          }
          const res = await piService.reserveInvoice(head.pi_code);
          const d = res.data?.data || {};
          message.success(
            draft
              ? `ออกเลข ${d.pi_code} แล้ว (จอง ${d.inv_code} ไว้ให้)`
              : `จองเลข ${d.inv_code || target} แล้ว`
          );
          /* เลขเอกสารเปลี่ยน → กลับไปหน้ารายการเพื่อไม่ให้ค้างที่คีย์เดิม */
          if (draft) navigate(gotoFrom, { replace: true });
          else loadDocument();
        } catch (err) {
          message.error(err?.response?.data?.message || "ทำรายการไม่สำเร็จ");
        }
      },
    });
  };

  /* ---------- ส่วนแสดงผล ---------- */
  const columns = detailColumns({ updateDetail, deleteDetail, currency, openPicker, readOnly: isView });

  const TitleBar = (
    <Flex justify="space-between" align="center" wrap gap={8}>
      <Space align="center" wrap>
        <Typography.Title level={3} className="m-0 !text-zinc-800">
          {config?.title || "Proforma Invoice"}
        </Typography.Title>
        {Number(head.is_draft) === 1 ? (
          <Tag color="default">ยังไม่ออกเลขเอกสาร</Tag>
        ) : head.pi_code ? (
          <Tag color="blue">{head.pi_code}</Tag>
        ) : null}
        {head.inv_code ? (
          <Tag color="green">ออก Invoice แล้ว : {head.inv_code}</Tag>
        ) : Number(head.inv_reserved) === 1 ? (
          <Tag color="gold">จองเลข {invCodeOf(head.runno)} ไว้</Tag>
        ) : null}
      </Space>

      <Space wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(gotoFrom, { replace: true })}>
          ย้อนกลับ
        </Button>
        {!isView && !isEdit && (
          <Button icon={<ImportOutlined />} onClick={() => setOpenInvoice(true)}>
            ดึงข้อมูลจาก Invoice
          </Button>
        )}
        {!isView && isEdit && !head.inv_code && (
          <Button
            icon={<NumberOutlined />}
            style={
              Number(head.inv_reserved) === 1
                ? { borderColor: "#fa8c16", color: "#fa8c16" }
                : undefined
            }
            onClick={handleReserve}
          >
            {Number(head.inv_reserved) === 1
              ? "ยกเลิกการจองเลข"
              : Number(head.is_draft) === 1
              ? "ออกเลขเอกสาร"
              : "จองเลข Invoice"}
          </Button>
        )}
        {!isView && (
          <Button icon={<SaveFilled />} loading={loading} onClick={() => handleSave(false)}>
            {isEdit ? "บันทึก" : "บันทึกเป็นใบร่าง"}
          </Button>
        )}
        {!isView && !isEdit && head.source_type !== "INVOICE" && (
          <Button
            type="primary"
            icon={<NumberOutlined />}
            loading={loading}
            onClick={() => handleSave(true)}
          >
            บันทึก + ออกเลขเอกสาร
          </Button>
        )}
        {!isView && !isEdit && head.source_type === "INVOICE" && (
          <Button type="primary" icon={<SaveFilled />} loading={loading} onClick={() => handleSave(false)}>
            บันทึก
          </Button>
        )}
      </Space>
    </Flex>
  );

  const TabGeneral = (
    <Row gutter={[16, 0]}>
      <Col xs={24} sm={12} md={5}>
        <Form.Item label="เลขที่ PI">
          <Input
            value={
              Number(head.is_draft) === 1
                ? "ยังไม่ออกเลข — กดปุ่มออกเลขเอกสารเมื่อพร้อม"
                : head.pi_code || ""
            }
            disabled
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} md={5}>
        <Form.Item label="วันที่เอกสาร" name="pi_date" rules={[{ required: true, message: "เลือกวันที่" }]}>
          <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} md={4}>
        <Form.Item label="สกุลเงิน" name="currency">
          <Select options={CURRENCIES.map((c) => ({ label: c, value: c }))} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} md={10}>
        <Form.Item label="ลูกค้าในระบบ" name="cuscode" tooltip="เว้นว่างได้ ถ้าจะพิมพ์ชื่อลูกค้าเอง">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="เลือกลูกค้า (ไม่บังคับ)"
            options={customers}
            onChange={(v) => {
              const c = customers.find((x) => x.value === v);
              if (c) form.setFieldsValue({ cusname: c.cusname, currency: c.currency || "USD" });
            }}
          />
        </Form.Item>
      </Col>

      <Col xs={24} sm={12} md={10}>
        <Form.Item label="ชื่อลูกค้า (พิมพ์เอง)" name="cusname">
          <Input placeholder="ใช้เมื่อไม่ได้เลือกลูกค้าจากระบบ" />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} md={5}>
        <Form.Item label="Brand" name="brand"><Input /></Form.Item>
      </Col>
      <Col xs={24} sm={12} md={5}>
        <Form.Item label="Customer PO Ref" name="customer_po"><Input /></Form.Item>
      </Col>
      <Col xs={24} sm={12} md={4}>
        <Form.Item label="Country of Origin" name="country_of_origin"><Input /></Form.Item>
      </Col>

      <Col xs={24}>
        <Form.Item label="หมายเหตุ" name="remark">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Col>
    </Row>
  );

  const TabConsignee = (
    <Row gutter={[16, 0]}>
      <Col xs={24} md={12}>
        <Form.Item label="ชื่อผู้รับสินค้า" name="consignee_name"><Input /></Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label="ผู้ติดต่อ" name="consignee_contact"><Input /></Form.Item>
      </Col>
      <Col xs={24}>
        <Form.Item label="ที่อยู่" name="consignee_address">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label="Email" name="consignee_email"><Input /></Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label="โทรศัพท์" name="consignee_tel"><Input /></Form.Item>
      </Col>
      <Col xs={24}>
        <Form.Item label="Notify party" name="notify_party" tooltip="เว้นว่าง = ใช้ข้อมูล Consignee เดียวกัน">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Col>
    </Row>
  );

  const TabShipping = (
    <Row gutter={[16, 0]}>
      <Col xs={24} sm={12} md={6}>
        <Form.Item label="Port of Loading" name="port_of_loading"><Input /></Form.Item>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Form.Item label="Place of receipt" name="place_of_receipt"><Input /></Form.Item>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Form.Item label="Final destination" name="final_destination"><Input /></Form.Item>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Form.Item label="Feeder &amp; Ocean Vessel" name="carrier"><Input /></Form.Item>
      </Col>

      <Col xs={12} sm={12} md={6}>
        <Form.Item label="ETD" name="etd">
          <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
        </Form.Item>
      </Col>
      <Col xs={12} sm={12} md={6}>
        <Form.Item label="ETA" name="eta">
          <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Form.Item label="Ship Via" name="ship_via"><Input /></Form.Item>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Form.Item label="Volume (CBM)" name="volume_cbm"><Input /></Form.Item>
      </Col>

      <Col xs={24} md={12}>
        <Form.Item label="Price Term (Delivery Term)" name="delivery_term"><Input /></Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label="Payment Term" name="payment_term"><Input /></Form.Item>
      </Col>
      <Col xs={12} md={6}>
        <Form.Item label="Net Weight (kgs)" name="net_weight"><Input /></Form.Item>
      </Col>
      <Col xs={12} md={6}>
        <Form.Item label="Gross Weight (kgs)" name="gross_weight"><Input /></Form.Item>
      </Col>
    </Row>
  );

  const TabBank = (
    <Row gutter={[16, 0]}>
      <Col xs={24} md={12}>
        <Form.Item label="Beneficiary" name="bank_beneficiary"><Input /></Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label="Bank Name" name="bank_name"><Input /></Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label="Branch" name="bank_branch"><Input /></Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label="Account No." name="bank_account"><Input /></Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item label="SWIFT Code" name="swift_code"><Input /></Form.Item>
      </Col>
      <Col xs={24}>
        <Form.Item label="Bank Address" name="bank_address"><Input /></Form.Item>
      </Col>
    </Row>
  );

  return (
    <div className="invoice-manage">
      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        {TitleBar}

        <Card loading={loading}>
          {head.source_type === "MANUAL" && (
            <Alert
              type="info"
              showIcon
              className="!mb-3"
              message="ใบนี้กรอกชื่อสินค้าได้เลย ไม่ต้องใส่รหัสสินค้า — ตอนนำไปออก Invoice ระบบจะบังคับให้จับคู่กับสินค้าในระบบก่อน"
            />
          )}
          {head.source_type === "INVOICE" && head.inv_code && (
            <Alert
              type="success"
              showIcon
              className="!mb-3"
              message={`ดึงข้อมูลมาจาก Invoice ${head.inv_code} — ใช้เลขรันเดียวกัน`}
            />
          )}

          <Form form={form} layout="vertical" autoComplete="off" disabled={isView}>
            <Tabs
              items={[
                { key: "general", label: "ข้อมูลทั่วไป", forceRender: true, children: TabGeneral },
                { key: "consignee", label: "ผู้รับสินค้า", forceRender: true, children: TabConsignee },
                { key: "shipping", label: "ขนส่ง & เงื่อนไข", forceRender: true, children: TabShipping },
                { key: "bank", label: "ธนาคาร", forceRender: true, children: TabBank },
              ]}
            />
          </Form>
        </Card>

        <Card>
          <Table
            title={() => (
              <Flex justify="space-between" align="center">
                <Typography.Text strong>รายการสินค้า</Typography.Text>
                {!isView && (
                  <Button size="small" icon={<PlusOutlined />} onClick={addRow}>
                    เพิ่มรายการ
                  </Button>
                )}
              </Flex>
            )}
            size="small"
            rowKey={(r) => r._rid || r.id || r.seq}
            columns={columns}
            dataSource={detail}
            pagination={false}
            scroll={{ x: "max-content" }}
            bordered
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={9} align="right">
                  <Typography.Text strong>รวมทั้งสิ้น</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9} align="right">
                  <Typography.Text strong>
                    {total.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={10} />
              </Table.Summary.Row>
            )}
          />
        </Card>
      </Space>

      <ModalPickProduct
        open={openProduct}
        close={setOpenProduct}
        confirm={handlePickProduct}
        hintName={pickIndex > -1 ? detail[pickIndex]?.pg_name : ""}
      />
      <ModalPickInvoice open={openInvoice} close={setOpenInvoice} confirm={handlePickInvoice} />
    </div>
  );
}
